import { db } from '../firebaseConfig';
import { collection, addDoc } from 'firebase/firestore';

/**
 * Log an activity to the audit trail
 * @param {Object} activityData - The activity data to log
 * @param {string} activityData.action - The action performed (e.g., 'Login', 'User Created')
 * @param {string} activityData.user - The user who performed the action
 * @param {string} activityData.details - Description of the action
 * @param {string} [activityData.target] - The target of the action (optional)
 * @param {string} [activityData.userId] - The user ID who performed the action (optional)
 * @param {string} [activityData.page] - The page where the action occurred (optional)
 * @param {string} [activityData.ipAddress] - The IP address of the user (optional)
 * @param {string} [activityData.userAgent] - The user agent string (optional)
 */
export async function logActivity(activityData) {
  try {
    const activity = {
      action: activityData.action,
      user: activityData.user,
      details: activityData.details,
      target: activityData.target || null,
      userId: activityData.userId || null,
      page: activityData.page || null,
      ipAddress: activityData.ipAddress || null,
      userAgent: activityData.userAgent || null,
      timestamp: new Date(),
      createdAt: new Date().toISOString()
    };

    await addDoc(collection(db, 'activities'), activity);
    console.log('Activity logged:', activity);
  } catch (error) {
    console.error('Error logging activity:', error);
  }
}

// Activity types constants
export const ACTIVITY_TYPES = {
  // Authentication
  LOGIN: 'Login',
  LOGOUT: 'Logout',
  LOGIN_FAILED: 'Login Failed',
  PASSWORD_RESET_REQUESTED: 'Password Reset Requested',
  PASSWORD_RESET_COMPLETED: 'Password Reset Completed',
  
  // User Management
  USER_CREATED: 'User Created',
  USER_UPDATED: 'User Updated',
  USER_DELETED: 'User Deleted',
  USER_VIEWED: 'User Viewed',
  USER_STATUS_CHANGED: 'User Status Changed',
  USER_ROLE_CHANGED: 'User Role Changed',
  
  // Client Management
  CLIENT_CREATED: 'Client Created',
  CLIENT_UPDATED: 'Client Updated',
  CLIENT_DELETED: 'Client Deleted',
  CLIENT_VIEWED: 'Client Viewed',
  CLIENT_SEARCHED: 'Client Searched',
  CLIENT_EXPORTED: 'Client Exported',
  
  // Member Management
  MEMBER_INVITED: 'Member Invited',
  MEMBER_VIEWED: 'Member Viewed',
  MEMBER_UPDATED: 'Member Updated',
  MEMBER_DELETED: 'Member Deleted',
  
  // Question & Answer
  QUESTION_CREATED: 'Question Created',
  QUESTION_UPDATED: 'Question Updated',
  QUESTION_DELETED: 'Question Deleted',
  QUESTION_VIEWED: 'Question Viewed',
  QUESTION_RESPONDED: 'Question Responded',
  QUESTION_STATUS_CHANGED: 'Question Status Changed',
  QUESTION_ASSIGNED: 'Question Assigned',
  
  // Profile Management
  PROFILE_UPDATED: 'Profile Updated',
  PROFILE_VIEWED: 'Profile Viewed',
  PASSWORD_CHANGED: 'Password Changed',
  
  // Navigation & Page Views
  PAGE_VIEWED: 'Page Viewed',
  DASHBOARD_ACCESSED: 'Dashboard Accessed',
  ACTIVITY_LOG_ACCESSED: 'Activity Log Accessed',
  REPORTS_ACCESSED: 'Reports Accessed',
  
  // Search & Filter
  SEARCH_PERFORMED: 'Search Performed',
  FILTER_APPLIED: 'Filter Applied',
  SORT_CHANGED: 'Sort Changed',
  
  // Data Operations
  DATA_EXPORTED: 'Data Exported',
  DATA_IMPORTED: 'Data Imported',
  BULK_ACTION_PERFORMED: 'Bulk Action Performed',
  
  // System Actions
  SYSTEM_SETTINGS_CHANGED: 'System Settings Changed',
  BACKUP_CREATED: 'Backup Created',
  LOGS_CLEARED: 'Logs Cleared',
  
  // Email & Notifications
  EMAIL_SENT: 'Email Sent',
  EMAIL_FAILED: 'Email Failed',
  NOTIFICATION_SENT: 'Notification Sent',
  
  // Security
  SUSPICIOUS_ACTIVITY: 'Suspicious Activity',
  ACCESS_DENIED: 'Access Denied',
  PERMISSION_VIOLATION: 'Permission Violation'
};

// Helper functions for common activities
export const ActivityLogger = {
  // Authentication
  login: (user, userId, page = null) => logActivity({
    action: ACTIVITY_TYPES.LOGIN,
    user: user,
    userId: userId,
    page: page,
    details: 'User logged into the system'
  }),

  logout: (user, userId, page = null) => logActivity({
    action: ACTIVITY_TYPES.LOGOUT,
    user: user,
    userId: userId,
    page: page,
    details: 'User logged out of the system'
  }),

  loginFailed: (email, reason = 'Invalid credentials') => logActivity({
    action: ACTIVITY_TYPES.LOGIN_FAILED,
    user: email,
    details: `Login failed: ${reason}`
  }),

  // User Management
  userCreated: (createdBy, createdByUserId, targetUser, page = null) => logActivity({
    action: ACTIVITY_TYPES.USER_CREATED,
    user: createdBy,
    userId: createdByUserId,
    target: targetUser,
    page: page,
    details: 'Created new user account'
  }),

  userUpdated: (updatedBy, updatedByUserId, targetUser, page = null) => logActivity({
    action: ACTIVITY_TYPES.USER_UPDATED,
    user: updatedBy,
    userId: updatedByUserId,
    target: targetUser,
    page: page,
    details: 'Updated user account information'
  }),

  userDeleted: (deletedBy, deletedByUserId, targetUser, page = null) => logActivity({
    action: ACTIVITY_TYPES.USER_DELETED,
    user: deletedBy,
    userId: deletedByUserId,
    target: targetUser,
    page: page,
    details: 'Deleted user account'
  }),

  userViewed: (viewedBy, viewedByUserId, targetUser, page = null) => logActivity({
    action: ACTIVITY_TYPES.USER_VIEWED,
    user: viewedBy,
    userId: viewedByUserId,
    target: targetUser,
    page: page,
    details: 'Viewed user details'
  }),

  userStatusChanged: (changedBy, changedByUserId, targetUser, newStatus, page = null) => logActivity({
    action: ACTIVITY_TYPES.USER_STATUS_CHANGED,
    user: changedBy,
    userId: changedByUserId,
    target: targetUser,
    page: page,
    details: `Changed user status to ${newStatus}`
  }),

  userRoleChanged: (changedBy, changedByUserId, targetUser, newRole, page = null) => logActivity({
    action: ACTIVITY_TYPES.USER_ROLE_CHANGED,
    user: changedBy,
    userId: changedByUserId,
    target: targetUser,
    page: page,
    details: `Changed user role to ${newRole}`
  }),

  // Client Management
  clientCreated: (createdBy, createdByUserId, clientName, page = null) => logActivity({
    action: ACTIVITY_TYPES.CLIENT_CREATED,
    user: createdBy,
    userId: createdByUserId,
    target: clientName,
    page: page,
    details: 'Added new client to the system'
  }),

  clientUpdated: (updatedBy, updatedByUserId, clientName, page = null) => logActivity({
    action: ACTIVITY_TYPES.CLIENT_UPDATED,
    user: updatedBy,
    userId: updatedByUserId,
    target: clientName,
    page: page,
    details: 'Updated client information'
  }),

  clientDeleted: (deletedBy, deletedByUserId, clientName, page = null) => logActivity({
    action: ACTIVITY_TYPES.CLIENT_DELETED,
    user: deletedBy,
    userId: deletedByUserId,
    target: clientName,
    page: page,
    details: 'Deleted client from the system'
  }),

  clientViewed: (viewedBy, viewedByUserId, clientName, page = null) => logActivity({
    action: ACTIVITY_TYPES.CLIENT_VIEWED,
    user: viewedBy,
    userId: viewedByUserId,
    target: clientName,
    page: page,
    details: 'Viewed client details'
  }),

  clientSearched: (searchedBy, searchedByUserId, searchTerm, page = null) => logActivity({
    action: ACTIVITY_TYPES.CLIENT_SEARCHED,
    user: searchedBy,
    userId: searchedByUserId,
    target: searchTerm,
    page: page,
    details: 'Searched for clients'
  }),

  clientExported: (exportedBy, exportedByUserId, exportType, page = null) => logActivity({
    action: ACTIVITY_TYPES.CLIENT_EXPORTED,
    user: exportedBy,
    userId: exportedByUserId,
    target: exportType,
    page: page,
    details: 'Exported client data'
  }),

  // Member Management
  memberInvited: (invitedBy, invitedByUserId, invitedContact, page = null) => logActivity({
    action: ACTIVITY_TYPES.MEMBER_INVITED,
    user: invitedBy,
    userId: invitedByUserId,
    target: invitedContact,
    page: page,
    details: 'Sent invitation to join the system'
  }),

  memberViewed: (viewedBy, viewedByUserId, targetMember, page = null) => logActivity({
    action: ACTIVITY_TYPES.MEMBER_VIEWED,
    user: viewedBy,
    userId: viewedByUserId,
    target: targetMember,
    page: page,
    details: 'Viewed member details'
  }),

  memberUpdated: (updatedBy, updatedByUserId, targetMember, page = null) => logActivity({
    action: ACTIVITY_TYPES.MEMBER_UPDATED,
    user: updatedBy,
    userId: updatedByUserId,
    target: targetMember,
    page: page,
    details: 'Updated member information'
  }),

  memberDeleted: (deletedBy, deletedByUserId, targetMember, page = null) => logActivity({
    action: ACTIVITY_TYPES.MEMBER_DELETED,
    user: deletedBy,
    userId: deletedByUserId,
    target: targetMember,
    page: page,
    details: 'Deleted member from the system'
  }),

  // Question & Answer
  questionCreated: (createdBy, createdByUserId, questionTitle, page = null) => logActivity({
    action: ACTIVITY_TYPES.QUESTION_CREATED,
    user: createdBy,
    userId: createdByUserId,
    target: questionTitle,
    page: page,
    details: 'Created new question'
  }),

  questionUpdated: (updatedBy, updatedByUserId, questionTitle, updateType = 'updated', page = null) => logActivity({
    action: ACTIVITY_TYPES.QUESTION_UPDATED,
    user: updatedBy,
    userId: updatedByUserId,
    target: questionTitle,
    page: page,
    details: `Question ${updateType}`
  }),

  questionDeleted: (deletedBy, deletedByUserId, questionTitle, page = null) => logActivity({
    action: ACTIVITY_TYPES.QUESTION_DELETED,
    user: deletedBy,
    userId: deletedByUserId,
    target: questionTitle,
    page: page,
    details: 'Deleted question'
  }),

  questionViewed: (viewedBy, viewedByUserId, questionTitle, page = null) => logActivity({
    action: ACTIVITY_TYPES.QUESTION_VIEWED,
    user: viewedBy,
    userId: viewedByUserId,
    target: questionTitle,
    page: page,
    details: 'Viewed question details'
  }),

  questionResponded: (respondedBy, respondedByUserId, questionTitle, page = null) => logActivity({
    action: ACTIVITY_TYPES.QUESTION_RESPONDED,
    user: respondedBy,
    userId: respondedByUserId,
    target: questionTitle,
    page: page,
    details: 'Responded to question'
  }),

  questionStatusChanged: (changedBy, changedByUserId, questionTitle, newStatus, page = null) => logActivity({
    action: ACTIVITY_TYPES.QUESTION_STATUS_CHANGED,
    user: changedBy,
    userId: changedByUserId,
    target: questionTitle,
    page: page,
    details: `Changed question status to ${newStatus}`
  }),

  questionAssigned: (assignedBy, assignedByUserId, questionTitle, assignedTo, page = null) => logActivity({
    action: ACTIVITY_TYPES.QUESTION_ASSIGNED,
    user: assignedBy,
    userId: assignedByUserId,
    target: questionTitle,
    page: page,
    details: `Assigned question to ${assignedTo}`
  }),

  // Profile Management
  profileUpdated: (user, userId, page = null) => logActivity({
    action: ACTIVITY_TYPES.PROFILE_UPDATED,
    user: user,
    userId: userId,
    page: page,
    details: 'Updated profile information'
  }),

  profileViewed: (user, userId, page = null) => logActivity({
    action: ACTIVITY_TYPES.PROFILE_VIEWED,
    user: user,
    userId: userId,
    page: page,
    details: 'Viewed profile information'
  }),

  passwordChanged: (user, userId, page = null) => logActivity({
    action: ACTIVITY_TYPES.PASSWORD_CHANGED,
    user: user,
    userId: userId,
    page: page,
    details: 'Changed account password'
  }),

  // Navigation & Page Views
  pageViewed: (user, userId, pageName, page = null) => logActivity({
    action: ACTIVITY_TYPES.PAGE_VIEWED,
    user: user,
    userId: userId,
    target: pageName,
    page: page,
    details: `Viewed ${pageName} page`
  }),

  dashboardAccessed: (user, userId, page = null) => logActivity({
    action: ACTIVITY_TYPES.DASHBOARD_ACCESSED,
    user: user,
    userId: userId,
    page: page,
    details: 'Accessed dashboard'
  }),

  activityLogAccessed: (user, userId, page = null) => logActivity({
    action: ACTIVITY_TYPES.ACTIVITY_LOG_ACCESSED,
    user: user,
    userId: userId,
    page: page,
    details: 'Accessed activity log'
  }),

  reportsAccessed: (user, userId, page = null) => logActivity({
    action: ACTIVITY_TYPES.REPORTS_ACCESSED,
    user: user,
    userId: userId,
    page: page,
    details: 'Accessed reports section'
  }),

  // Search & Filter
  searchPerformed: (user, userId, searchTerm, searchType, page = null) => logActivity({
    action: ACTIVITY_TYPES.SEARCH_PERFORMED,
    user: user,
    userId: userId,
    target: searchTerm,
    page: page,
    details: `Performed ${searchType} search`
  }),

  filterApplied: (user, userId, filterType, filterValue, page = null) => logActivity({
    action: ACTIVITY_TYPES.FILTER_APPLIED,
    user: user,
    userId: userId,
    target: filterValue,
    page: page,
    details: `Applied ${filterType} filter`
  }),

  sortChanged: (user, userId, sortField, sortDirection, page = null) => logActivity({
    action: ACTIVITY_TYPES.SORT_CHANGED,
    user: user,
    userId: userId,
    target: `${sortField} (${sortDirection})`,
    page: page,
    details: 'Changed data sorting'
  }),

  // Data Operations
  dataExported: (user, userId, exportType, recordCount, page = null) => logActivity({
    action: ACTIVITY_TYPES.DATA_EXPORTED,
    user: user,
    userId: userId,
    target: exportType,
    page: page,
    details: `Exported ${recordCount} records as ${exportType}`
  }),

  dataImported: (user, userId, importType, recordCount, page = null) => logActivity({
    action: ACTIVITY_TYPES.DATA_IMPORTED,
    user: user,
    userId: userId,
    target: importType,
    page: page,
    details: `Imported ${recordCount} records from ${importType}`
  }),

  bulkActionPerformed: (user, userId, actionType, recordCount, page = null) => logActivity({
    action: ACTIVITY_TYPES.BULK_ACTION_PERFORMED,
    user: user,
    userId: userId,
    target: actionType,
    page: page,
    details: `Performed bulk ${actionType} on ${recordCount} records`
  }),

  // Email & Notifications
  emailSent: (user, userId, emailType, recipient, page = null) => logActivity({
    action: ACTIVITY_TYPES.EMAIL_SENT,
    user: user,
    userId: userId,
    target: recipient,
    page: page,
    details: `Sent ${emailType} email`
  }),

  emailFailed: (user, userId, emailType, recipient, error, page = null) => logActivity({
    action: ACTIVITY_TYPES.EMAIL_FAILED,
    user: user,
    userId: userId,
    target: recipient,
    page: page,
    details: `Failed to send ${emailType} email: ${error}`
  }),

  notificationSent: (user, userId, notificationType, recipient, page = null) => logActivity({
    action: ACTIVITY_TYPES.NOTIFICATION_SENT,
    user: user,
    userId: userId,
    target: recipient,
    page: page,
    details: `Sent ${notificationType} notification`
  }),

  // Security
  suspiciousActivity: (user, userId, activity, page = null) => logActivity({
    action: ACTIVITY_TYPES.SUSPICIOUS_ACTIVITY,
    user: user,
    userId: userId,
    target: activity,
    page: page,
    details: 'Suspicious activity detected'
  }),

  accessDenied: (user, userId, resource, reason, page = null) => logActivity({
    action: ACTIVITY_TYPES.ACCESS_DENIED,
    user: user,
    userId: userId,
    target: resource,
    page: page,
    details: `Access denied: ${reason}`
  }),

  permissionViolation: (user, userId, action, resource, page = null) => logActivity({
    action: ACTIVITY_TYPES.PERMISSION_VIOLATION,
    user: user,
    userId: userId,
    target: resource,
    page: page,
    details: `Permission violation: ${action} on ${resource}`
  }),

  // Legacy methods for backward compatibility
  statusChanged: (changedBy, changedByUserId, targetUser, newStatus, page = null) => logActivity({
    action: ACTIVITY_TYPES.USER_STATUS_CHANGED,
    user: changedBy,
    userId: changedByUserId,
    target: targetUser,
    page: page,
    details: `Changed user status to ${newStatus}`
  })
}; 